import { createCountField } from './count-field.js';

interface EstimatePickerOptions extends Omit<Parameters<typeof createCountField>[1], 'stepValue'> {
  sliderLabel: string;
  unansweredText: string;
  formatValue(value: number): string;
}

// Ordinal slider positions preserve useful precision across practice sizes.
const ACTIVE_PATIENT_STOPS = [
  ...Array.from({ length: 21 }, (_, index) => index * 100),
  ...Array.from({ length: 12 }, (_, index) => 2250 + index * 250),
  ...Array.from({ length: 10 }, (_, index) => 5500 + index * 500),
];
const SLIDER_MAXIMUM = ACTIVE_PATIENT_STOPS.at(-1)!;

function stepEstimate(current: number | undefined, direction: -1 | 1): number {
  if (current === undefined) return 100;
  if (current > SLIDER_MAXIMUM) return current + direction * 500;
  if (direction === 1) return ACTIVE_PATIENT_STOPS.find(value => value > current) ?? current + 500;
  const next = ACTIVE_PATIENT_STOPS.findIndex(value => value >= current);
  return ACTIVE_PATIENT_STOPS[Math.max(0, next - 1)]!;
}

/** Exact submitted count with optional graduated, ordinal estimate controls. */
export function createEstimatePicker(input: HTMLInputElement, options: EstimatePickerOptions) {
  const element = document.createElement('div'); element.className = 'estimate-picker';
  const count = createCountField(input, { ...options, stepValue: stepEstimate });
  count.element.classList.add('estimate-picker-controls');
  const slider = document.createElement('input'); slider.type = 'range'; slider.className = 'range-picker-slider';
  slider.min = '0'; slider.max = String(ACTIVE_PATIENT_STOPS.length - 1); slider.step = '1';
  slider.setAttribute('aria-label', options.sliderLabel);
  const ticks = document.createElement('div'); ticks.className = 'range-picker-ticks'; ticks.setAttribute('aria-hidden', 'true');
  for (const value of [0, 2000, 5000, SLIDER_MAXIMUM]) {
    const tick = document.createElement('span'); tick.className = 'range-picker-tick';
    tick.style.left = `${100 * ACTIVE_PATIENT_STOPS.indexOf(value) / (ACTIVE_PATIENT_STOPS.length - 1)}%`;
    tick.textContent = options.formatValue(value);
    if (value > 0 && value < SLIDER_MAXIMUM) tick.classList.add('range-picker-tick-middle');
    ticks.append(tick);
  }
  element.append(count.element, slider, ticks);
  let disposed = false;
  const unavailable = () => input.matches(':disabled') || input.readOnly;
  const parsed = () => /^\d+$/.test(input.value) && !count.error() ? Number(input.value) : undefined;
  function sync() {
    if (disposed) return;
    count.sync();
    const value = parsed();
    const nearest = value === undefined ? 0 : ACTIVE_PATIENT_STOPS.reduce((best, stop, index) =>
      Math.abs(stop - value) < Math.abs(ACTIVE_PATIENT_STOPS[best]! - value) ? index : best, 0);
    slider.value = String(nearest);
    slider.disabled = unavailable();
    element.dataset.unanswered = String(input.value === '');
    element.dataset.custom = String(value !== undefined && !ACTIVE_PATIENT_STOPS.includes(value));
    // The thumb has ordinal positions; assistive technology hears the exact count.
    slider.setAttribute('aria-valuemin', String(options.min));
    slider.setAttribute('aria-valuemax', String(Math.max(SLIDER_MAXIMUM, value ?? 0)));
    if (value === undefined) slider.removeAttribute('aria-valuenow');
    else slider.setAttribute('aria-valuenow', String(value));
    slider.setAttribute('aria-valuetext', value === undefined ? (input.value || options.unansweredText) : options.formatValue(value));
    slider.setAttribute('aria-required', String(input.required));
    slider.setAttribute('aria-readonly', String(input.readOnly));
    for (const name of ['aria-invalid', 'aria-describedby', 'aria-errormessage']) {
      const attribute = input.getAttribute(name);
      if (attribute === null) slider.removeAttribute(name); else slider.setAttribute(name, attribute);
    }
    if (count.error()) slider.setAttribute('aria-invalid', 'true');
  }
  function choose(value: number) {
    if (disposed || unavailable()) { sync(); return; }
    if (!Number.isSafeInteger(value) || value < options.min || value > options.max) { sync(); return; }
    if (input.value === String(value)) return;
    input.value = String(value); sync();
    input.dispatchEvent(new Event('input', { bubbles: true }));
    input.dispatchEvent(new Event('change', { bubbles: true }));
  }
  const onSlide = (event: Event) => {
    event.stopPropagation();
    choose(ACTIVE_PATIENT_STOPS[Number(slider.value)]!);
  };
  const onKey = (event: KeyboardEvent) => {
    if (event.isComposing || event.altKey || event.ctrlKey || event.metaKey || event.shiftKey) return;
    const current = parsed();
    let value: number;
    switch (event.key) {
      case 'Home': value = 0; break;
      case 'End': value = SLIDER_MAXIMUM; break;
      case 'ArrowUp': case 'ArrowRight': case 'PageUp': value = current === undefined ? 0 : stepEstimate(current, 1); break;
      case 'ArrowDown': case 'ArrowLeft': case 'PageDown': value = current === undefined ? 0 : stepEstimate(current, -1); break;
      default: return;
    }
    event.preventDefault(); choose(value);
  };
  const onReset = (event: Event) => { if (event.target === input.form) queueMicrotask(sync); };
  for (const type of ['input', 'change', 'invalid']) input.addEventListener(type, sync);
  for (const type of ['input', 'change', 'pointerup', 'click']) slider.addEventListener(type, onSlide);
  slider.addEventListener('keydown', onKey);
  document.addEventListener('reset', onReset);
  const observer = new MutationObserver(records => {
    if (records.some(record => record.target instanceof Element && (record.target === input || record.target.contains(input)))) sync();
  });
  observer.observe(document.documentElement, { subtree: true, childList: true, attributes: true,
    attributeFilter: ['disabled', 'readonly', 'required', 'aria-invalid', 'aria-describedby', 'aria-errormessage'] });
  sync();
  return {
    element, sync, error: count.error,
    dispose() {
      if (disposed) return;
      disposed = true; observer.disconnect(); count.dispose();
      for (const type of ['input', 'change', 'invalid']) input.removeEventListener(type, sync);
      for (const type of ['input', 'change', 'pointerup', 'click']) slider.removeEventListener(type, onSlide);
      slider.removeEventListener('keydown', onKey);
      document.removeEventListener('reset', onReset);
    },
  };
}
